import {
    useCallback,
} from 'react'
import {
    useNavigate,
    useParams,
} from 'react-router-dom'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'

import useGetLongTermLeaseInvoice from 'app/requests/LongTermLeaseInvoice/hooks/useGetLongTermLeaseInvoice'
import ButtonContainer from 'app/shared-components/ButtonContainer'
import Button from 'app/shared-components/Button'
import useLabel from 'app/hooks/useLabel'
import StatusHandler from 'app/shared-components/StatusHandler'
import useGetLongTermLeaseFees from 'app/requests/ContractBasis/hooks/useGetLongTermLeaseFees'
import useGetBillingCompany from 'app/Apps/ContactManagement/BillingCompanies/hooks/useGetBillingCompany'
import {
    InvoiceStatus,
} from 'app/enums/label.enums'
import useSendLongTermLeaseInvoiceToSap from 'app/requests/LongTermLeaseInvoice/hooks/useSendLongTermLeaseInvoiceToSap'

import useLongTermLeaseInvoicesContext from '../hooks/useLongTermLeaseInvoicesContext'
import GeneralInfo from './GeneralInfo'
import InvoiceForm from './InvoiceForm'
import LongTermLeaseInvoicePosition from './LongTermLeaseInvoicePosition'
import LongTermLeaseFees from './LongTermLeaseFees'
import useStyles from './longTermLeaseInvoicesUpdate.styles'

const LongTermLeaseInvoicesUpdate = () => {
    const l = useLabel()

    const {
        classes,
    } = useStyles()

    const {
        invoiceId,
    } = useParams()

    const navigate = useNavigate()

    const navigateBack = useCallback(() => {
        navigate('..')
    }, [navigate])

    const {
        longTermLease,
    } = useLongTermLeaseInvoicesContext()

    const {
        data: longTermLeaseInvoice,
        invalidate,
    } = useGetLongTermLeaseInvoice({
        longTermLeaseInvoiceId: Number(invoiceId),
    })

    const {
        data: longTermLeaseFees,
    } = useGetLongTermLeaseFees({
        longTermLeaseFeesId: longTermLeaseInvoice?.longTermLeaseFeesId,
    })

    const {
        data: billingCompany,
        isDataReady,
        isFetching,
        isError,
        error,
    } = useGetBillingCompany(longTermLeaseFees?.billingCompany?.id)

    const {
        mutate: sendToSapRequest,
        isPending,
    } = useSendLongTermLeaseInvoiceToSap({
        onSuccess: invalidate,
    })

    const sendToSap = useCallback((): void => {
        sendToSapRequest({
            longTermLeaseInvoiceId: Number(invoiceId),
        })
    }, [
        invoiceId,
        sendToSapRequest,
    ])

    return (
        <StatusHandler
            isSuccess={isDataReady}
            isFetching={isFetching}
            isError={isError}
            error={error}
        >
            <>
                <div className={classes.backToTableText}>
                    <ArrowBackIosIcon />
                    <a
                        onKeyDown={navigateBack}
                        onClick={navigateBack}
                    >
                        {l('BACK_TO_INVOICES')}
                    </a>
                </div>
                <GeneralInfo
                    longTermLease={longTermLease}
                    longTermLeaseFees={longTermLeaseFees}
                    billingCompany={billingCompany}
                />
                <InvoiceForm
                    longTermLeaseInvoice={longTermLeaseInvoice}
                    onSuccess={invalidate}
                    longTermLeaseId={longTermLease?.id}
                />
                <LongTermLeaseInvoicePosition
                    longTermLeaseInvoiceId={Number(invoiceId)}
                    currency={longTermLeaseFees?.currency}
                    className={classes.longTermLeasePosition}
                />
                <ButtonContainer>
                    {longTermLeaseInvoice?.invoiceStatus === InvoiceStatus.CREATED && (
                        <Button
                            label="Send to Sap"
                            data-testid="send-to-sap"
                            onClick={sendToSap}
                            saving={isPending}
                        />
                    )}
                </ButtonContainer>
                <LongTermLeaseFees
                    fees={longTermLeaseFees?.fees}
                    currency={longTermLeaseFees?.currency}
                />
            </>
        </StatusHandler>
    )
}

export default LongTermLeaseInvoicesUpdate
